import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { CREATE_ACCOUNT, ADD_USER_TO_ACCOUNT } from '../../graphql/mutations';
import { GET_USER_ACCOUNTS, GET_SOCIAL_PROFILES } from '../../graphql';
import { X, CheckCircle } from 'lucide-react';
import { GetSocialProfilesResponse } from '../../types/graphql';
import { CreateAccountInput, AddUserToAccountInput } from '../../types/graphql';
import { Platform } from '../../types';
import { PlatformIcon } from '../common/PlatformIcon';
import { useChannelConnect } from '../reach/ChannelPicker';

interface OnboardingModalProps {
  onComplete?: () => void;
  onSkip?: () => void;
  initialStep?: number;
  initialAccountId?: string | null;
  initialAccountPlan?: string;
}

export function OnboardingModal({
  onComplete,
  onSkip,
  initialStep = 1,
  initialAccountId = null,
  initialAccountPlan = 'basic',
}: OnboardingModalProps) {
  const [currentStep, setCurrentStep] = useState<number>(initialStep);
  const [organizationName, setOrganizationName] = useState<string>('');
  const [newUserEmail, setNewUserEmail] = useState<string>('');
  const [newUserRole, setNewUserRole] = useState<string>('member');
  const [createdAccountId, setCreatedAccountId] = useState<string | null>(initialAccountId);

  const { refetch: refetchAccounts } = useQuery(GET_USER_ACCOUNTS);
  const { data: socialProfilesData, refetch: refetchSocialProfiles } =
    useQuery<GetSocialProfilesResponse>(GET_SOCIAL_PROFILES, {
      variables: { accountId: createdAccountId },
      skip: !createdAccountId,
    });

  // Update currentStep when initialStep prop changes
  useEffect(() => {
    setCurrentStep(initialStep);
  }, [initialStep]);

  // Update createdAccountId when initialAccountId prop changes
  useEffect(() => {
    if (initialAccountId) {
      setCreatedAccountId(initialAccountId);
    }
  }, [initialAccountId]);

  const [createAccount] = useMutation<{ createAccount: { id: string } }>(CREATE_ACCOUNT, {
    onCompleted: (data) => {
      setCreatedAccountId(data.createAccount.id);
      refetchAccounts();
      setCurrentStep(2);
    },
    onError: (error) => {
      console.error('Error creating account:', error);
      // TODO: Add error handling UI
    },
  });

  // The sign-in popup comes back with the channels it can post to; the
  // person picks (several) or the one is connected at once.
  const { start: startChannelSignIn, picker: channelPicker } = useChannelConnect({
    accountId: createdAccountId,
    onConnected: () => void refetchSocialProfiles(),
  });

  const [addUserToAccount] = useMutation(ADD_USER_TO_ACCOUNT, {
    onCompleted: () => {
      // Move to completion or stay on step 3 for additional invites
      setNewUserEmail('');
      setNewUserRole('member');
    },
    onError: (error) => {
      console.error('Error adding user to account:', error);
      // TODO: Add error handling UI
    },
  });

  const handleCreateOrganization = () => {
    if (!organizationName.trim()) return;
    createAccount({
      variables: {
        input: {
          name: organizationName.trim(),
        } as CreateAccountInput,
      },
    });
  };

  const getPlatformColor = (platform: string | undefined): string => {
    switch (platform?.toLowerCase()) {
      case 'facebook':
        return '#1877F2';
      case 'instagram':
        return '#E4405F';
      case 'x':
        return '#000000';
      case 'threads':
        return '#000000';
      case 'youtube':
        return '#FF0000';
      case 'tiktok':
        return '#000000';
      case 'snapchat':
        return '#FFFC00';
      case 'reddit':
        return '#FF4500';
      case 'bluesky':
        return '#0560FF';
      case 'whatsapp':
        return '#25D366';
      case 'linkedin':
        return '#0A66C2';
      default:
        return '#f2efe0';
    }
  };

  const handlePlatformSelect = async (platform: string) => {
    if (!createdAccountId) {
      return;
    }
    // Move to next step; the channel appears once its sign-in is picked and connected.
    setCurrentStep(3);
    await startChannelSignIn(platform);
  };

  const handleInviteTeamMember = () => {
    if (!newUserEmail.trim() || !createdAccountId) return;
    // Note: This assumes the backend can handle email lookup
    // You may need to adjust based on your backend implementation
    addUserToAccount({
      variables: {
        input: {
          accountId: createdAccountId,
          email: newUserEmail, // May need to be user ID instead
          role: newUserRole,
        } as AddUserToAccountInput,
      },
    });
  };

  const handleComplete = () => {
    if (onComplete) {
      onComplete();
    }
  };

  const handleSkip = () => {
    if (onSkip) {
      onSkip();
    }
  };

  const hasSocialAccounts = (socialProfilesData?.socialProfiles?.length ?? 0) > 0;
  const accountPlan = initialAccountPlan;
  const productName = accountPlan === 'basic' ? 'tinycms' : `tinycms ${accountPlan}`;
  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-75">
      <div className="bg-cmp-raise rounded-cmp3 shadow-cmp-e3 border border-cmp-line max-w-2xl w-full m-4 max-h-[90vh] overflow-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold text-white">Welcome to {productName}!</h2>
            <button
              onClick={handleSkip}
              className="text-cmp-ink-2 hover:text-white transition-colors"
            >
              <X className="w-9" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-8">
            {[1, 2, 3].map((step) => (
              <React.Fragment key={step}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                      currentStep > step
                        ? 'bg-primary text-onfill'
                        : currentStep === step
                          ? 'bg-primary text-onfill ring-4 ring-primary ring-opacity-50'
                          : 'bg-form-dark text-cmp-ink-2'
                    }`}
                  >
                    {currentStep > step ? <CheckCircle className="w-9" /> : step}
                  </div>
                  <span className="mt-2 text-xs text-cmp-ink-2 text-center">
                    {step === 1 ? 'Organization' : step === 2 ? 'Social Account' : 'Team'}
                  </span>
                </div>
                {step < 3 && (
                  <div
                    className={`flex-1 h-1 mx-2 ${
                      currentStep > step ? 'bg-primary' : 'bg-form-dark'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Step 1: Create Organization */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div>
                <p className="text-white mb-4">
                  Thank you for choosing tinycms. To get started, you will need to set up your
                  Organization.
                </p>
                <label className="block text-sm font-medium text-white mb-1">
                  Organization Name
                </label>
                <input
                  type="text"
                  autoFocus
                  value={organizationName}
                  onChange={(e) => setOrganizationName(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && organizationName.trim()) {
                      handleCreateOrganization();
                    }
                  }}
                  className="w-full px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter your organization name"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button
                  onClick={handleSkip}
                  className="px-4 py-2 text-white border border-cmp-line-2 rounded hover:bg-form-dark transition-colors"
                >
                  Skip
                </button>
                <button
                  onClick={handleCreateOrganization}
                  disabled={!organizationName.trim()}
                  className="px-4 py-2 bg-primary text-onfill rounded font-medium hover:bg-primary-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Create Organization
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Connect Social Account */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div>
                <p className="text-white mb-4">
                  Now let's connect your first social media account. This will allow you to start
                  scheduling and managing posts.
                </p>
                <p className="text-sm text-cmp-ink-2 mb-6">
                  Select a platform to connect. Clicking an icon will create your account and start
                  the authorization process.
                </p>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4">
                  {[
                    { platform: 'facebook', label: 'Facebook' },
                    { platform: 'x', label: 'X' },
                    { platform: 'threads', label: 'Threads' },
                    { platform: 'instagram', label: 'Instagram' },
                    { platform: 'youtube', label: 'YouTube' },
                    { platform: 'tiktok', label: 'TikTok' },
                    { platform: 'snapchat', label: 'Snapchat' },
                    { platform: 'reddit', label: 'Reddit' },
                    { platform: 'bluesky', label: 'BlueSky' },
                    { platform: 'whatsapp', label: 'WhatsApp' },
                    { platform: 'linkedin', label: 'LinkedIn' },
                  ].map(({ platform, label }) => (
                    <button
                      key={platform}
                      onClick={() => handlePlatformSelect(platform)}
                      className="flex flex-col items-center gap-2 p-4 border border-cmp-line-2 rounded-lg hover:border-primary hover:bg-form-dark transition-all duration-200 group"
                    >
                      <div
                        className="w-12 h-12 flex items-center justify-center"
                        style={{ color: getPlatformColor(platform) }}
                      >
                        <PlatformIcon platform={platform as Platform} size={48} />
                      </div>
                      <span className="text-xs text-white text-center group-hover:text-primary transition-colors">
                        {label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex justify-between gap-2 pt-4">
                {initialStep === 1 && (
                  <button
                    onClick={() => setCurrentStep(1)}
                    className="px-4 py-2 text-white border border-cmp-line-2 rounded hover:bg-form-dark transition-colors"
                  >
                    Back
                  </button>
                )}
                {initialStep === 2 && <div />}
                <div className="flex gap-2">
                  <button
                    onClick={handleSkip}
                    className="px-4 py-2 text-white border border-cmp-line-2 rounded hover:bg-form-dark transition-colors"
                  >
                    Skip
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Invite Team Members (Optional) */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <div>
                <p className="text-white mb-4">
                  Great! You're all set up. Optionally, you can invite team members to collaborate
                  on your organization.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-white mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={newUserEmail}
                      onChange={(e) => setNewUserEmail(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && newUserEmail.trim()) {
                          handleInviteTeamMember();
                        }
                      }}
                      className="w-full px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="team.member@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-1">Role</label>
                    <select
                      value={newUserRole}
                      onChange={(e) => setNewUserRole(e.target.value)}
                      className="w-full px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="admin" className="bg-form-dark">
                        Admin
                      </option>
                      <option value="member" className="bg-form-dark">
                        Member
                      </option>
                    </select>
                  </div>
                </div>
                {hasSocialAccounts && (
                  <div className="mt-4 p-3 bg-form-dark rounded border border-primary border-opacity-50">
                    <p className="text-sm text-white">✓ Social account connected successfully!</p>
                  </div>
                )}
              </div>
              <div className="flex justify-between gap-2 pt-4">
                <button
                  onClick={() => setCurrentStep(2)}
                  className="px-4 py-2 text-white border border-cmp-line-2 rounded hover:bg-form-dark transition-colors"
                >
                  Back
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={handleComplete}
                    className="px-4 py-2 bg-primary text-onfill rounded font-medium hover:bg-primary-medium transition-colors"
                  >
                    Complete Setup
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {channelPicker}
    </div>
  );
}
